import { GenericObjectType } from "@rjsf/utils";
import { useMemo } from "react";

import { getRanaWidgetFromProperty } from "@/features/project-process-run-form";
import { useVectorPreview } from "@/hooks/use-vector-preview";
import { useFileQuery } from "@/queries/query-file";

import { VectorLayerSelectorFieldProps } from "../types/vector-layer-selector-field-props-type";

function getNestedValue(obj: GenericObjectType | undefined, path: string): unknown {
  const keys = path.split(".");
  let current: unknown = obj;
  for (const key of keys) {
    if (current && typeof current === "object" && key in current) {
      current = (current as GenericObjectType)[key];
    } else {
      return undefined;
    }
  }
  return current;
}

const getSelectedFileIdFromFormData = (
  formData: GenericObjectType | undefined,
  source: string | undefined
): string | undefined => {
  if (!source || !formData) return undefined;

  const sourceData = getNestedValue(formData, source);

  // Check if sourceData is an object and has an "id" property
  if (!sourceData || typeof sourceData !== "object" || !("id" in sourceData)) return undefined;

  // If the "id" property is a string, return it; otherwise, return undefined
  const rawId = sourceData.id;
  if (typeof rawId === "string") return rawId;

  return undefined;
};

export function useGetVectorFileForSelector(props: VectorLayerSelectorFieldProps) {
  const { schema, formContext } = props;
  const allFormData = formContext?.allFormData;

  // Get the widget configuration (check both top level and anyOf/oneOf)
  const widget = getRanaWidgetFromProperty(schema, "vector_layer_selector");
  const source = widget?.source;
  const allowedGeometryTypes = widget?.type;

  // Derive file ID from the source field (supports dot-notation paths like "buildings.vector")
  const selectedFileId = getSelectedFileIdFromFormData(allFormData, source);

  // Fetch file metadata
  const { data: file, isLoading: isLoadingFile } = useFileQuery(selectedFileId);

  // Fetch vector preview data to get layers
  const {
    isLoading: isLoadingPreview,
    layers: fileLayers,
    bounds: fileBounds,
    visibleLayers,
    setVisibleLayers,
  } = useVectorPreview({ descriptorId: file?.descriptorId });

  const descriptorId = file?.descriptorId ?? "none";

  // Filter layers by geometry type if specified
  const layers = useMemo(() => {
    if (!allowedGeometryTypes || allowedGeometryTypes.length === 0) {
      return fileLayers;
    }
    return fileLayers.filter(({ type }) => {
      if (!type) return true;
      return allowedGeometryTypes.includes(type);
    });
  }, [fileLayers, allowedGeometryTypes]);

  const layerOptions = useMemo(
    () =>
      layers.map(({ layer }) => ({
        id: layer,
        name: layer,
      })),
    [layers]
  );

  const bounds: [number, number, number, number] | null = fileBounds ?? null;

  return {
    descriptorId,
    layers,
    layerOptions,
    isLoading: isLoadingFile || isLoadingPreview,
    bounds,
    file,
    visibleLayers,
    setVisibleLayers,
  } as const;
}
